import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Typography,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ASSIGNMENT_TYPE_OPTIONS = [
  { value: 'training', label: 'Training' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'document', label: 'Document Review' },
  { value: 'log', label: 'Log' },
];

const COMPLETION_CRITERIA_OPTIONS = [
  { value: 'any', label: 'Any assignee completes' },
  { value: 'all', label: 'All assignees must complete' },
  { value: 'specific', label: 'Specific number of assignees' },
];



interface AssignmentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
  viewMode?: boolean;
  viewData?: any;
  validationErrors?: any[];
  isValidating?: boolean;
  onFormChange?: () => void;
  onValidationResolved?: () => void;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  initialData, 
  viewMode = false,
  viewData,
  validationErrors = [],
  isValidating = false,
  onFormChange,
  onValidationResolved
}) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [validationResolved, setValidationResolved] = useState(false);

  // Reset validation resolved state when validation errors change
  useEffect(() => {
    console.log('AssignmentForm: validation errors changed:', validationErrors);
    if (validationErrors.length === 0) {
      setValidationResolved(false);
    }
  }, [validationErrors]);

  // Debug logging for component renders
  useEffect(() => {
    console.log('AssignmentForm render: validationErrors.length =', validationErrors.length, 'validationResolved =', validationResolved);
  });

  const [form, setForm] = useState<{
    assignment_type: string;
    content_type: string;
    object_id: string;
    assigned_by_id: number;
    assignee_ids: number[];
    due_date: string;
    completion_criteria: string;
    required_completions: number;
    status: string;
    is_active: boolean;
    send_reminders: boolean;
    reminder_frequency_days: number;
    is_recurring: boolean;
    recurrence_mode: string;
    recurrence_unit: string;
    lead_time_days: number;
    fixed_weekday: number | null;
    fixed_day_of_month: number | null;
    fixed_eom: boolean;
    fixed_month: number | null;
    fixed_month_day: number | null;
  }>({
    assignment_type: 'training',
    content_type: '',
    object_id: '',
    assigned_by_id: user?.id || 1, // Automatically set to current user
    assignee_ids: [],
    due_date: '',
    completion_criteria: 'all',
    required_completions: 1,
    status: 'pending', // Always 'pending' for new assignments
    is_active: true, // Always true for new assignments
    send_reminders: true,
    reminder_frequency_days: 7,
    is_recurring: false,
    recurrence_mode: 'last_completion',
    recurrence_unit: 'year',
    lead_time_days: 30,
    fixed_weekday: null,
    fixed_day_of_month: null,
    fixed_eom: false,
    fixed_month: null,
    fixed_month_day: null,
  });

  const [availableContent, setAvailableContent] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Fetch available content and users
  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchAvailableContent();
    }
  }, [open, form.assignment_type]);

  const fetchUsers = async () => {
    try {
      // Only fetch users the current requester can assign to
      const response = await api.get('/users/assignable/');
      setUsers(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const fetchAvailableContent = async () => {
    try {
      setLoading(true);
      let endpoint = '';
      switch (form.assignment_type) {
        case 'training':
          endpoint = '/trainings/';
          break;
        case 'checklist':
          endpoint = '/checklists/';
          break;
        case 'document':
          endpoint = '/documents/';
          break;
        case 'log':
          endpoint = '/log-templates/';
          break;
        default:
          setAvailableContent([]);
          setLoading(false);
          return;
      }
      
      const response = await api.get(endpoint);
      let content = response.data.results || response.data;
      
      // For documents and trainings, only show current versions
      if (form.assignment_type === 'document' || form.assignment_type === 'training' || form.assignment_type === 'log') {
        content = content.filter((item: any) => item.is_current_version);
      }
      
      setAvailableContent(content);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setAvailableContent([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      setForm({
        assignment_type: initialData.assignment_type || 'training',
        content_type: initialData.content_type || '',
        object_id: initialData.object_id || '',
        assigned_by_id: initialData.assigned_by?.id || user?.id || 1, // Automatically set to current user
        assignee_ids: initialData.assignees?.map((u: any) => u.id) || [],
        due_date: initialData.due_date ? initialData.due_date.split('T')[0] : '',
        completion_criteria: initialData.completion_criteria || 'all',
        required_completions: initialData.required_completions || 1,
        status: initialData.status || 'pending',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        send_reminders: initialData.send_reminders !== undefined ? initialData.send_reminders : true,
        reminder_frequency_days: initialData.reminder_frequency_days || 7,
        is_recurring: initialData.is_recurring !== undefined ? initialData.is_recurring : false,
        recurrence_mode: initialData.recurrence_mode || 'last_completion',
        recurrence_unit: initialData.recurrence_unit || 'year',
        lead_time_days: initialData.lead_time_days || 30,
        fixed_weekday: initialData.fixed_weekday || null,
        fixed_day_of_month: initialData.fixed_day_of_month || null,
        fixed_eom: initialData.fixed_eom || false,
        fixed_month: initialData.fixed_month || null,
        fixed_month_day: initialData.fixed_month_day || null,
      });
    } else if (viewData) {
      setForm({
        assignment_type: viewData.assignment_type || 'training',
        content_type: viewData.content_type || '',
        object_id: viewData.object_id || '',
        assigned_by_id: viewData.assigned_by?.id || user?.id || 1, // Automatically set to current user
        assignee_ids: viewData.assignees?.map((u: any) => u.id) || [],
        due_date: viewData.due_date ? viewData.due_date.split('T')[0] : '',
        completion_criteria: viewData.completion_criteria || 'all',
        required_completions: viewData.required_completions || 1,
        status: viewData.status || 'pending',
        is_active: viewData.is_active !== undefined ? viewData.is_active : true,
        send_reminders: viewData.send_reminders !== undefined ? viewData.send_reminders : true,
        reminder_frequency_days: viewData.reminder_frequency_days || 7,
        is_recurring: viewData.is_recurring !== undefined ? viewData.is_recurring : false,
        recurrence_mode: viewData.recurrence_mode || 'last_completion',
        recurrence_unit: viewData.recurrence_unit || 'year',
        lead_time_days: viewData.lead_time_days || 30,
        fixed_weekday: viewData.fixed_weekday || null,
        fixed_day_of_month: viewData.fixed_day_of_month || null,
        fixed_eom: viewData.fixed_eom || false,
        fixed_month: viewData.fixed_month || null,
        fixed_month_day: viewData.fixed_month_day || null,
      });
    } else {
      setForm({
        assignment_type: 'training',
        content_type: '',
        object_id: '',
        assigned_by_id: user?.id || 1, // Automatically set to current user
        assignee_ids: [],
        due_date: '',
        completion_criteria: 'all',
        required_completions: 1,
        status: 'pending',
        is_active: true,
        send_reminders: true,
        reminder_frequency_days: 7,
        is_recurring: false,
        recurrence_mode: 'last_completion',
        recurrence_unit: 'year',
        lead_time_days: 30,
        fixed_weekday: null,
        fixed_day_of_month: null,
        fixed_eom: false,
        fixed_month: null,
        fixed_month_day: null,
      });
    }
  }, [initialData, viewData, open, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'assignment_type' ? { object_id: '' } : {}),
    }));
    // Clear validation errors when form changes
    if (onFormChange) {
      onFormChange();
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value === '' ? 0 : parseInt(value, 10),
    }));
    // Clear validation errors when form changes
    if (onFormChange) {
      onFormChange();
    }
  };

  const handleNullableNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value === '' ? null : parseInt(value, 10),
    }));
    // Clear validation errors when form changes
    if (onFormChange) {
      onFormChange();
    }
  };

  const handleSubmit = () => {
    const formData = {
      ...form,
      content_type_str: form.assignment_type,
      object_id: parseInt(form.object_id, 10),
      assigned_by_id: form.assigned_by_id,
      assignee_ids: form.assignee_ids,
    };
    delete (formData as any).content_type;
    
    onSubmit(formData);
  };

  // Form validation
  const isFormValid = () => {
    // Check if all required fields are populated
    const requiredFields = [
      form.assignment_type,
      form.object_id,
      form.assignee_ids.length > 0,
      form.due_date
    ];
    
    // If recurring, check required recurrence fields
    if (form.is_recurring) {
      if (form.recurrence_mode === 'fixed_calendar') {
        if (form.recurrence_unit === 'week' && form.fixed_weekday === null) {
          return false;
        }
        if (form.recurrence_unit === 'month' && !form.fixed_eom && form.fixed_day_of_month === null) {
          return false;
        }
        if (form.recurrence_unit === 'year' && (form.fixed_month === null || form.fixed_month_day === null)) {
          return false;
        }
      }
    }
    
    // All required fields must be truthy
    return requiredFields.every(field => Boolean(field));
  };

  const selectedContent = availableContent.find(
    (item: any) => item.id.toString() === String(form.object_id)
  );
  const contentDisplayName =
    viewData?.content_title ||
    initialData?.content_title ||
    selectedContent?.title ||
    selectedContent?.name ||
    (form.object_id ? `Content #${form.object_id}` : 'Unknown');
  const selectedAssignees = users.filter((user: any) => form.assignee_ids.includes(user.id));
  const assignedByUser = users.find((user: any) => user.id === form.assigned_by_id);

  // Handle duplicate assignment resolution
  const handleRemoveDuplicates = () => {
    // Remove duplicate users from the current assignment
    const duplicateUserIds = validationErrors.map(error => error.user_id);
    const filteredAssignees = form.assignee_ids.filter((id: number) => !duplicateUserIds.includes(id));

    setForm(prev => ({
      ...prev,
      assignee_ids: filteredAssignees
    }));

    // Clear validation errors and mark as resolved
    onValidationResolved?.();
    setValidationResolved(true);
  };

  const handleDeleteExistingAssignments = async () => {
    try {
      setLoading(true);

      // Delete the existing assignments
      const deletePromises = validationErrors.map(error =>
        api.delete(`/assignments/${error.assignment_id}/`)
      );

      await Promise.all(deletePromises);

      // Clear validation errors and mark as resolved
      onValidationResolved?.();
      setValidationResolved(true);
      setLoading(false);

      // Show success message
      alert(`Successfully deleted ${validationErrors.length} existing assignment(s). You can now create the new assignment.`);
    } catch (error: any) {
      setLoading(false);
      console.error('Error deleting existing assignments:', error);

      if (error.response?.status === 403) {
        alert('You do not have permission to delete assignments. Please contact an administrator.');
      } else if (error.response?.status === 404) {
        alert('One or more assignments could not be found. They may have already been deleted.');
      } else {
        alert('Failed to delete existing assignments. Please try again or contact support.');
      }
    }
  };

  const handleUpdateExistingAssignments = async () => {
    try {
      setLoading(true);

      // Update existing assignments to extend due date or change status
      const updatePromises = validationErrors.map(error =>
        api.patch(`/assignments/${error.assignment_id}/`, {
          due_date: form.due_date,
          status: 'pending' // Reset to pending so they can be reassigned
        })
      );

      await Promise.all(updatePromises);

      // Clear validation errors and mark as resolved
      onValidationResolved?.();
      setValidationResolved(true);
      setLoading(false);

      alert(`Successfully updated ${validationErrors.length} existing assignment(s). You can now create the new assignment.`);
    } catch (error: any) {
      setLoading(false);
      console.error('Error updating existing assignments:', error);

      if (error.response?.status === 403) {
        alert('You do not have permission to update assignments. Please contact an administrator.');
      } else if (error.response?.status === 404) {
        alert('One or more assignments could not be found. They may have already been deleted.');
      } else {
        alert('Failed to update existing assignments. Please try again or contact support.');
      }
    }
  };

  const handleProceedAnyway = () => {
    // Show confirmation dialog before proceeding with duplicate assignments
    const confirmed = window.confirm(
      `Are you sure you want to create assignments for users who already have this ${validationErrors[0]?.assignment_type}? This will create duplicate assignments.`
    );

    if (confirmed) {
      // Clear validation errors and mark as resolved
      onValidationResolved?.();
      setValidationResolved(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {viewMode ? 'View Assignment' : (initialData ? 'Edit Assignment' : 'Create Assignment')}
      </DialogTitle>
      <DialogContent>
        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Duplicate Assignment Detected
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The following users already have assignments for this {validationErrors[0]?.assignment_type}:
            </Typography>
            {validationErrors.map((error, index) => (
              <Box key={index} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2">
                  • <strong>{error.user_name}</strong> - {error.content_title} ({error.status})
                </Typography>
              </Box>
            ))}
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Choose an action to resolve this conflict:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleRemoveDuplicates()}
                startIcon={<DeleteIcon />}
              >
                Remove from Assignment
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (window.confirm(`Are you sure you want to delete ${validationErrors.length} existing assignment(s)? This action cannot be undone.`)) {
                    handleDeleteExistingAssignments();
                  }
                }}
                startIcon={<DeleteIcon />}
                color="error"
              >
                Delete Existing Assignments
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (window.confirm(`Are you sure you want to update ${validationErrors.length} existing assignment(s)? This will reset their status to pending.`)) {
                    handleUpdateExistingAssignments();
                  }
                }}
                startIcon={<EditIcon />}
              >
                Update Existing Assignments
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleProceedAnyway()}
                startIcon={<CheckCircleIcon />}
                color="warning"
              >
                Create Anyway
              </Button>
            </Box>
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Content Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Content Selection</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Assignment Type"
              name="assignment_type"
              value={form.assignment_type}
              onChange={handleChange}
              select
              fullWidth
              required
              disabled={viewMode}
            >
              {ASSIGNMENT_TYPE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Select Content"
              name="object_id"
              value={form.object_id}
              onChange={handleChange}
              select
              fullWidth
              required
              disabled={viewMode || loading}
              helperText={loading ? 'Loading content...' : `Select a ${form.assignment_type} to assign`}
            >
              {availableContent.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title || item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Assignment Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Assignment Details</Typography>
          </Grid>
          

          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Due Date"
              name="due_date"
              type="date"
              value={form.due_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              disabled={viewMode}
            />
          </Grid>

          {/* Assignees */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Assignees <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(option: any) => `${option.first_name} ${option.last_name} (${option.email})`}
              value={selectedAssignees}
              onChange={(_, newValue) => {
                setForm(prev => ({
                  ...prev,
                  assignee_ids: newValue.map((user: any) => user.id)
                }));
                // Clear validation errors when assignees change
                if (onFormChange) {
                  onFormChange();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label="Select Assignees"
                  placeholder="Choose users to assign this to"
                  required
                  disabled={viewMode}
                  helperText={form.assignee_ids.length === 0 ? "At least one assignee is required" : `${form.assignee_ids.length} assignee(s) selected`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={`${option.first_name} ${option.last_name} (${option.email})`}
                      {...chipProps}
                      size="small"
                    />
                  );
                })
              }
            />
          </Grid>

          {/* Completion Criteria */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Completion Criteria</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Completion Criteria"
              name="completion_criteria"
              value={form.completion_criteria}
              onChange={handleChange}
              select
              fullWidth
              disabled={viewMode}
            >
              {COMPLETION_CRITERIA_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          {form.completion_criteria === 'specific' && (
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Required Completions"
                name="required_completions"
                type="number"
                value={form.required_completions}
                onChange={handleNumberChange}
                fullWidth
                disabled={viewMode}
                inputProps={{ min: 1, max: selectedAssignees.length }}
                helperText={`Max: ${selectedAssignees.length} assignees`}
              />
            </Grid>
          )}

          {/* Reminders */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Reminders</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="send_reminders"
                  checked={form.send_reminders}
                  onChange={handleChange}
                  disabled={viewMode}
                />
              }
              label="Send Reminders"
            />
          </Grid>
          
          {form.send_reminders && (
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Reminder Frequency (days)"
                name="reminder_frequency_days"
                type="number"
                value={form.reminder_frequency_days}
                onChange={handleNumberChange}
                fullWidth
                disabled={viewMode}
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>
          )}

          {/* Recurrence */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Recurrence</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_recurring"
                  checked={form.is_recurring}
                  onChange={handleChange}
                  disabled={viewMode}
                />
              }
              label="Is Recurring"
            />
          </Grid>

          {form.is_recurring && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Recurrence Mode"
                  name="recurrence_mode"
                  value={form.recurrence_mode}
                  onChange={handleChange}
                  select
                  fullWidth
                  disabled={viewMode}
                >
                  <MenuItem value="last_completion">Last Completion Date</MenuItem>
                  <MenuItem value="fixed_calendar">Fixed Calendar Cycle</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Recurrence Unit"
                  name="recurrence_unit"
                  value={form.recurrence_unit}
                  onChange={handleChange}
                  select
                  fullWidth
                  disabled={viewMode}
                >
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                  <MenuItem value="quarter">Quarterly</MenuItem>
                  <MenuItem value="year">Yearly</MenuItem>
                </TextField>
              </Grid>

              {form.recurrence_mode === 'fixed_calendar' && (
                <>
                  {form.recurrence_unit === 'week' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        margin="dense"
                        label="Weekday (0=Mon, 6=Sun)"
                        name="fixed_weekday"
                        type="number"
                        value={form.fixed_weekday || ''}
                        onChange={handleNullableNumberChange}
                        fullWidth
                        disabled={viewMode}
                        inputProps={{ min: 0, max: 6 }}
                        helperText="0=Monday, 6=Sunday"
                      />
                    </Grid>
                  )}

                  {form.recurrence_unit === 'month' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          margin="dense"
                          label="Day of Month"
                          name="fixed_day_of_month"
                          type="number"
                          value={form.fixed_day_of_month || ''}
                          onChange={handleNullableNumberChange}
                          fullWidth
                          disabled={viewMode}
                          inputProps={{ min: 1, max: 31 }}
                          helperText="Day of the month (1-31)"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="fixed_eom"
                              checked={form.fixed_eom}
                              onChange={handleChange}
                              disabled={viewMode}
                            />
                          }
                          label="Use Last Day of Month"
                        />
                      </Grid>
                    </>
                  )}

                  {form.recurrence_unit === 'year' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          margin="dense"
                          label="Month"
                          name="fixed_month"
                          type="number"
                          value={form.fixed_month || ''}
                          onChange={handleNullableNumberChange}
                          fullWidth
                          disabled={viewMode}
                          inputProps={{ min: 1, max: 12 }}
                          helperText="Month (1-12)"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          margin="dense"
                          label="Day of Month"
                          name="fixed_month_day"
                          type="number"
                          value={form.fixed_month_day || ''}
                          onChange={handleNullableNumberChange}
                          fullWidth
                          disabled={viewMode}
                          inputProps={{ min: 1, max: 31 }}
                          helperText="Day of the month (1-31)"
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Lead Time (days)"
                  name="lead_time_days"
                  type="number"
                  value={form.lead_time_days}
                  onChange={handleNumberChange}
                  fullWidth
                  disabled={viewMode}
                  inputProps={{ min: 0 }}
                  helperText="Days before due date to create new assignment"
                />
              </Grid>
            </>
          )}

          {/* View Mode Display */}
          {viewMode && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Assignment Details</Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Content:</strong> {contentDisplayName}
                </Typography>
                <Typography variant="body2">
                  <strong>Assigned By:</strong> {assignedByUser ? `${assignedByUser.first_name} ${assignedByUser.last_name} (${assignedByUser.email})` : 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  <strong>Assignees:</strong> {selectedAssignees.map(u => `${u.first_name} ${u.last_name} (${u.email})`).join(', ') || 'None'}
                </Typography>
                <Typography variant="body2">
                  <strong>Due Date:</strong> {form.due_date || 'No due date'}
                </Typography>
                <Typography variant="body2">
                  <strong>Completion Criteria:</strong> {COMPLETION_CRITERIA_OPTIONS.find(o => o.value === form.completion_criteria)?.label}
                </Typography>
                {form.completion_criteria === 'specific' && (
                  <Typography variant="body2">
                    <strong>Required Completions:</strong> {form.required_completions}
                  </Typography>
                )}
                {form.is_recurring && (
                  <>
                    <Typography variant="body2">
                      <strong>Recurrence:</strong> Yes
                    </Typography>
                    <Typography variant="body2">
                      <strong>Mode:</strong> {form.recurrence_mode === 'last_completion' ? 'Last Completion Date' : 'Fixed Calendar Cycle'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Unit:</strong> {form.recurrence_unit.charAt(0).toUpperCase() + form.recurrence_unit.slice(1)}
                    </Typography>
                    {form.recurrence_mode === 'fixed_calendar' && (
                      <>
                        {form.recurrence_unit === 'week' && form.fixed_weekday !== null && (
                          <Typography variant="body2">
                            <strong>Weekday:</strong> {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][form.fixed_weekday]}
                          </Typography>
                        )}
                        {form.recurrence_unit === 'month' && (
                          <>
                            {form.fixed_eom ? (
                              <Typography variant="body2">
                                <strong>Schedule:</strong> Last day of month
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                <strong>Schedule:</strong> Day {form.fixed_day_of_month} of month
                              </Typography>
                            )}
                          </>
                        )}
                        {form.recurrence_unit === 'year' && form.fixed_month && form.fixed_month_day && (
                          <Typography variant="body2">
                            <strong>Schedule:</strong> {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][form.fixed_month - 1]} {form.fixed_month_day}
                          </Typography>
                        )}
                      </>
                    )}
                    <Typography variant="body2">
                      <strong>Lead Time:</strong> {form.lead_time_days} days
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          )}

          {/* Validation Summary */}
          {!viewMode && !isFormValid() && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, mt: 2 }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                  Please complete the following required fields:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {!form.assignment_type && <li>Assignment Type</li>}
                  {!form.object_id && <li>Content Selection</li>}
                  {form.assignee_ids.length === 0 && <li>At least one Assignee</li>}
                  {!form.due_date && <li>Due Date</li>}
                </ul>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!viewMode && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!isFormValid() || isValidating || (validationErrors.length > 0 && !validationResolved)}
            startIcon={isValidating ? <CircularProgress size={20} /> : null}
          >
            {isValidating 
              ? 'Validating...' 
              : initialData ? 'Save Changes' : 'Create Assignment'
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentForm; 